import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../config/api';
import PageLayout from '../components/PageLayout';
import type { AnalyticsHubs } from '../components/AnalyticsHub';
import '../components/AnalyticsHub.css';

interface HubsResponse {
  hubs: AnalyticsHubs;
  note?: string;
}

const DEFAULT_HUBS: AnalyticsHubs = {
  ga4_product_ui_url: 'https://analytics.google.com/',
  cw_dashboard_url:
    'https://us-east-2.console.aws.amazon.com/cloudwatch/home?region=us-east-2#dashboards:name=stagespotter-analytics',
  athena_workgroup: 'stagespotter-analytics',
  athena_console_url:
    'https://us-east-2.console.aws.amazon.com/athena/home?region=us-east-2#/workgroups/details/stagespotter-analytics',
};

const UsagePage: React.FC = () => {
  const { token } = useAuth();
  const [hubs, setHubs] = useState<AnalyticsHubs>(DEFAULT_HUBS);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const response = await apiRequest<HubsResponse>(`/admin/analytics/hubs`, token);
        if (response.hubs) setHubs({ ...DEFAULT_HUBS, ...response.hubs });
        setNote(response.note || '');
        setError('');
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load analytics hubs');
      }
    })();
  }, [token]);

  return (
    <PageLayout title="API Usage">
      <div className="analytics-hub">
        <p className="analytics-hub-blurb">
          Per-request Dynamo API usage rollups for the consumer UI key are being retired. Use CloudWatch
          (API Lambda metrics / Logs Insights for <code>api_request</code>) and the analytics dashboard
          below for operational traffic.
        </p>
        {note && <p className="analytics-hub-note">{note}</p>}
        {error && <div className="analytics-hub-error">{error}</div>}
        <div className="analytics-hub-links">
          <a href={hubs.cw_dashboard_url} target="_blank" rel="noopener noreferrer">
            CloudWatch dashboard
          </a>
          <a href={hubs.ga4_product_ui_url} target="_blank" rel="noopener noreferrer">
            Google Analytics (Product)
          </a>
          <a
            href={hubs.athena_console_url || DEFAULT_HUBS.athena_console_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Athena ({hubs.athena_workgroup})
          </a>
        </div>
      </div>
    </PageLayout>
  );
};

export default UsagePage;
